import React, { useEffect, useState } from 'react';
import {
  Typography,
  IconButton,
  MenuItem,
  Toolbar,
  Button,
  Menu,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import KeycloakService from '../../services/KeycloakService';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import Logout from '@mui/icons-material/Logout';
import {
  Routing,
  getHomeRouteForMode,
  getModeForPathPrefix,
} from '../../utils/Routing';
import MenuIcon from '@mui/icons-material/Menu';
import './Toolbar.css';
import '../../App.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmployeeRoutes } from '../../routes';
import { useDispatch } from 'react-redux';
import ToolbarNotifications from './ToolbarNotifications';

const ALL_APP_MODES = [
  'Admin',
  'HR',
  'Project-Admin',
  'Finance',
  'Senior-Manager',
  'User',
  'Tech-Council',
];

const PROFILE_PATH =
  EmployeeRoutes.find((obj) => obj.title === 'Profile')?.path;

/** Single batched mode-flag transition for AppContent (avoids clear-all then set-one). */
const applyModeFlags = (mode, setters) => {
  const {
    setIsAdmin,
    setReportsSelected,
    setHRSelected,
    setFinanceSelected,
    setSeniorManagerSelected,
    setTechCouncilSelected,
    setProjectAdminSelected,
  } = setters;

  setIsAdmin(mode === 'Admin');
  setReportsSelected(mode === 'Report');
  setHRSelected(mode === 'HR');
  setFinanceSelected(mode === 'Finance');
  if (setSeniorManagerSelected) {
    setSeniorManagerSelected(mode === 'Senior-Manager');
  }
  setTechCouncilSelected(mode === 'Tech-Council');
  setProjectAdminSelected(mode === 'Project-Admin');
};

export default function ToolbarCompnent(props) {
  const {
    setIsAdmin,
    setReportsSelected,
    setHRSelected,
    setFinanceSelected,
    setSeniorManagerSelected,
    open,
    setOpen,
    roles,
    setTechCouncilSelected,
    setProjectAdminSelected,
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [modes, setModes] = useState(ALL_APP_MODES);
  const [selectedMode, setSelectedMode] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const modeSetters = {
    setIsAdmin,
    setReportsSelected,
    setHRSelected,
    setFinanceSelected,
    setSeniorManagerSelected,
    setTechCouncilSelected,
    setProjectAdminSelected,
  };

  const onDestroy = () => {
    dispatch({
      type: 'PEOPLE_PAGE',
      data: {
        id: '',
      },
    });
  };

  const navigateRoute = (routePath) => {
    navigate(routePath);
    if (props.hideMenu) props.hideMenu(false);
    onDestroy();
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const pathHasRole = (mode, path) => {
    if (mode === 'User') {
      if (path.startsWith('/user-manage')) {
        return KeycloakService.hasRole(['manager']);
      }
      return KeycloakService.hasRole(['employee']);
    }
    return KeycloakService.hasRole([mode.toLowerCase()]);
  };

  const syncMode = (mode) => {
    if (!mode || mode === selectedMode) return;
    localStorage.setItem('mode', mode);
    applyModeFlags(mode, modeSetters);
    setSelectedMode(mode);
  };

  useEffect(() => {
    const filteredModes = ALL_APP_MODES.filter((mode) => {
      if (mode === 'User') {
        return (
          KeycloakService.hasRole(['employee']) ||
          KeycloakService.hasRole(['manager'])
        );
      }
      return KeycloakService.hasRole([mode.toLowerCase()]);
    });

    setModes((prev) => {
      if (
        prev.length === filteredModes.length &&
        prev.every((mode, index) => mode === filteredModes[index])
      ) {
        return prev;
      }
      return filteredModes;
    });

    const path = location.pathname;
    const pathMode = getModeForPathPrefix(path);
    let expectedMode;

    if (pathMode != null) {
      expectedMode = pathHasRole(pathMode, path)
        ? pathMode
        : filteredModes[0];
    } else {
      const mode = localStorage.getItem('mode');
      if (mode == null) {
        expectedMode = KeycloakService.hasRole(['admin']) ? 'Admin' : 'User';
      } else {
        expectedMode = mode;
      }
    }

    syncMode(expectedMode);
    setLoader(false);
    // Only re-sync when the path (or derived mode expectation) changes — not on every render.
    // selectedMode is read intentionally; syncMode no-ops when unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    if (newMode === selectedMode) return;
    localStorage.setItem('mode', newMode);
    applyModeFlags(newMode, modeSetters);
    setSelectedMode(newMode);
    navigate(getHomeRouteForMode(newMode), { replace: true });
  };

  return (
    <>
      <Toolbar className="space-between-container">
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div">
          <button
            onClick={() => navigate(Routing(location.pathname))}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            <img
              src="/assets/images/logo.png"
              alt="toolbar-logo"
              className="toolbar-logo"
            />
          </button>
        </Typography>
        {roles.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {!loader && modes.length > 1 && (
                <FormControl sx={{ mt: 1, minWidth: 120 }} size="small">
                  <InputLabel id="role-select-label">App</InputLabel>
                  <Select
                    labelId="role-select-label"
                    id="role-select"
                    value={selectedMode}
                    label="Role"
                    defaultValue={selectedMode}
                    onChange={handleModeChange}
                  >
                    {modes.map((mode) => (
                      <MenuItem key={mode} value={mode}>
                        {mode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Typography variant="h6" noWrap component="div">
                <ToolbarNotifications />

                <Button onClick={(event) => handleMenu(event)}>
                  <img src="/assets/images/Avatar.png" alt="user-avatar" />
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigateRoute(PROFILE_PATH);
                    }}
                  >
                    <ListItemIcon>
                      <AccountBoxIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>
                  <MenuItem onClick={KeycloakService.doLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" color="primary" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Typography>
            </div>
          </>
        )}
      </Toolbar>
    </>
  );
}
